import React, { PureComponent } from 'react';
import { MeasureDto, MeasureId, RegulationOrMcdmOnlyDto, ReroutingDto } from 'app/model/CORE';
import {
    DetailedSolution,
    MeasureImpact,
    RegulationImpact,
    ReroutedFlightDto,
    ReroutingImpact,
    Solution,
    TvMeteoAlert,
} from 'app/model/SOL49';
import { Flex } from '../../common/Flex';
import { SINGLE_SELECTION, SingleSelection } from 'app/common/table/ManagedTable';
import { RegulationImpactPanel } from './RegulationImpactPanel';
import memoizeOne from 'memoize-one';
import MeasureUtils from '../../../common/util/MeasureUtils';
import { ReroutingImpactPanel } from './ReroutingImpactPanel';
import { Button, Popover, Space } from 'antd';
import AppContext from '../../../common/context/AppContext';
import './MeasureImpactPanel.less';
import ConditionalRender from '../../../common/components/ConditionalRender';
import { SolutionUtils } from 'app/common/util/SolutionUtils';
import { SolutionMeasures } from './SolutionMeasures';
import { TvLoadImpact } from './TvLoadImpact';
import { DeltaTvImpact } from './DeltaTvImpact';
import { DeltaMeasuresImpact } from './DeltaMeasuresImpact';
import Utils from 'app/common/util/Utils';

interface MeasureImpactPanelProps {
    solution: DetailedSolution;
    simplifiedDisplay?: boolean;
    meteoAlerts: { [key: string]: Array<TvMeteoAlert> };
    solutionImpactOutdated?: boolean;

    onRemoveMeasure: (measure: MeasureDto, solution: DetailedSolution) => void;
    onShowTVsImpact?: () => void;
    onShowOtherTVsImpact?: () => void;
    onShowMeasuresImpact?: () => void;
    onMeasureEdited?: (measureId: MeasureId, measure: MeasureDto) => void;
    onUpdateSolution?: (solution: Solution) => void;
    onApplySolution?: (solution: Solution) => void;
    onApplicableScenarios?: (applicableScenarios: Map<string, number>) => void;
    onShareSolution: (solution: DetailedSolution) => void;
    onShowOnMap?: () => void;
    onRrtFlightShowOnMap?: (flights: ReroutedFlightDto[]) => void;
}

interface MeasureImpactPanelState {
    selectedMeasure: MeasureDto;
}

export class MeasureImpactPanel extends PureComponent<MeasureImpactPanelProps, MeasureImpactPanelState> {
    constructor(props) {
        super(props);
        this.state = {
            selectedMeasure: props.solution?.measures[0],
        };
    }

    componentDidUpdate(prevProps: MeasureImpactPanelProps) {
        if (this.props.solution.name !== prevProps.solution.name) {
            // New solution selected, select first measure
            this.setState({
                selectedMeasure: this.props.solution.measures[0],
            });
        } else if (this.props.solution.measures !== prevProps.solution.measures) {
            // Measure updated, keep same selection
            this.setState({
                selectedMeasure: this.props.solution.measures.find(
                    m => m.measureId.id === this.state.selectedMeasure?.measureId.id,
                ),
            });
        }
    }

    selection = memoizeOne(
        (selectedMeasure: MeasureDto): SingleSelection<MeasureDto> => ({
            selectionMode: SINGLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItem: selectedMeasure,
        }),
    );
    handleSelectionChange = (selection: MeasureDto) => {
        this.setState({ selectedMeasure: selection });
    };

    handleMeasureRemoved = (measureDto: MeasureDto, solution: DetailedSolution) => {
        if (this.state.selectedMeasure.measureId.id === measureDto.measureId.id) {
            this.setState({
                selectedMeasure: solution.measures.find(m => m.measureId.id !== measureDto.measureId.id),
            });
        }
        this.props.onRemoveMeasure(measureDto, solution);
    };

    handleRrtFlightShowOnMap = (flights: ReroutedFlightDto[]) => {
        if (this.props.onRrtFlightShowOnMap) {
            this.props.onRrtFlightShowOnMap(flights);
        }
    };

    isMeasuresUpdated = () => {
        return this.props?.solutionImpactOutdated;
    };

    render() {
        const { solution } = this.props;

        const className = 'measure-impact-panel' + (this.props?.solutionImpactOutdated ? ' outdated' : '');

        const measure = this.state.selectedMeasure;
        const measureImpact: MeasureImpact =
            measure?.measureId?.id && solution.solutionImpact?.measureImpacts[measure.measureId.id];

        const regulation = measure && MeasureUtils.isRegulationOrMcdmOnly(measure) ? measure : null;
        const regulationImpact =
            measure && MeasureUtils.isRegulationOrMcdmOnly(measure) ? (measureImpact as RegulationImpact) : null;
        const tvId = MeasureUtils.getTvLocation(measure).id;
        const monitoredTv =
            regulation && solution.solutionImpact?.tvImpact?.find(tvImpact => tvImpact.tvId === tvId)?.monitoredTv;
        const rerouting = measure && MeasureUtils.isRerouting(measure) ? measure : null;
        const reroutingImpact =
            measure && MeasureUtils.isRerouting(measure) ? (measureImpact as ReroutingImpact) : null;

        // Check that all the regulations of the solution are valid, ignore rerouting
        const isValid = this.props.solution.measures.every(
            measure => !MeasureUtils.isRegulationOrMcdmOnly(measure) || MeasureUtils.isValidForSimulation(measure),
        );
        return (
            <Flex className={className} direction="vertical">
                <Flex grow={false} style={{ paddingTop: '1em' }}>
                    {!this.props.simplifiedDisplay && (
                        <Flex
                            style={{
                                fontSize: '2em',
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                            }}>
                            {this.props.solution?.name}
                        </Flex>
                    )}
                    <Flex grow={false}>
                        <Space size={[8, 0]}>
                            <Popover
                                content={<DeltaTvImpact deltaTvImpacts={solution.solutionImpact?.deltaTvImpacts} />}>
                                <Button onClick={this.props.onShowTVsImpact}> TFVs</Button>
                            </Popover>
                            <Popover
                                content={
                                    <DeltaMeasuresImpact
                                        solutionMeasures={solution.measures.map(m => m.measureId.id)}
                                        deltaRegulations={solution.solutionImpact?.deltaRegulations}
                                    />
                                }>
                                <Button onClick={this.props.onShowMeasuresImpact}>Measures</Button>
                            </Popover>
                            <ConditionalRender condition={Utils.isExists(this.props.onShowOnMap)}>
                                <Button onClick={this.props.onShowOnMap}>MAP</Button>
                            </ConditionalRender>
                        </Space>
                    </Flex>
                </Flex>
                <TvLoadImpact solution={solution} meteoAlerts={this.props.meteoAlerts} />
                <SolutionMeasures
                    solution={solution}
                    selectedMeasure={this.state.selectedMeasure}
                    onSelectedMeasureChange={this.handleSelectionChange}
                    onRemoveMeasure={this.handleMeasureRemoved}
                />
                <Flex style={{ padding: '0 1em' }}>
                    {regulation && (
                        <RegulationImpactPanel
                            regulation={regulation}
                            simplifiedDisplay={this.props.simplifiedDisplay}
                            regulationImpact={regulationImpact}
                            onRegulationEdited={this.props.onMeasureEdited}
                            onApplicableScenarios={this.props.onApplicableScenarios}
                            meteoAlerts={this.props.meteoAlerts?.[tvId]}
                            monitoredTv={monitoredTv}
                        />
                    )}
                    {rerouting && (
                        <ReroutingImpactPanel
                            rerouting={rerouting}
                            reroutingImpact={reroutingImpact}
                            onReroutingEdited={this.props.onMeasureEdited}
                            onShowFlightOnMap={this.handleRrtFlightShowOnMap}
                        />
                    )}
                </Flex>
                <Flex grow={false} style={{ alignSelf: 'flex-end', paddingTop: '1em' }}>
                    <MeasureImpactButtons
                        solutionOutdated={this.props.solutionImpactOutdated}
                        applyButtonHidden={!AppContext.isNmvpBaseline()}
                        onApplySolution={() => this.props.onApplySolution(this.props.solution)}
                        updateSolutionDisabled={!this.isMeasuresUpdated() || !isValid}
                        onUpdateSolution={() => this.props.onUpdateSolution(this.props.solution)}
                        onShareSolution={() => this.props.onShareSolution(this.props.solution)}
                        shareButtonHidden={
                            !AppContext.isFmpCoordinationEnabled() ||
                            SolutionUtils.isInitialSolution(this.props.solution)
                        }
                        applyButtonDisabled={this.props.solutionImpactOutdated}
                    />
                </Flex>
            </Flex>
        );
    }
}

interface MeasureImpactButtonsProps {
    solutionOutdated: boolean;
    applyButtonHidden?: boolean;
    applyButtonDisabled?: boolean;
    onApplySolution?: () => void;
    updateSolutionDisabled?: boolean;
    onUpdateSolution?: () => void;
    onShareSolution: () => void;
    shareButtonHidden: boolean;
}

class MeasureImpactButtons extends PureComponent<MeasureImpactButtonsProps> {
    render() {
        return (
            <Space>
                <ConditionalRender condition={!this.props.shareButtonHidden && !this.props.solutionOutdated}>
                    <Button key="share-solution" onClick={this.props.onShareSolution}>
                        Share
                    </Button>
                </ConditionalRender>
                <ConditionalRender condition={!this.props.applyButtonHidden}>
                    <Button
                        key="apply-solution"
                        disabled={this.props.applyButtonDisabled}
                        onClick={this.props.onApplySolution}>
                        Apply
                    </Button>
                </ConditionalRender>
                <Button
                    key="update-solution"
                    disabled={this.props.updateSolutionDisabled}
                    onClick={this.props.onUpdateSolution}>
                    Update impact
                </Button>
            </Space>
        );
    }
}
